import React, { useState, useMemo } from 'react';
import { InputGroup, InfoBox, formatCurrencyExt } from '../Common';

const fmtCur = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });

const RuinTool = () => {
    const [wr, setWr] = useState(45);
    const [rr, setRr] = useState(1.5);
    const [cap, setCap] = useState(1000000);
    const [rpt, setRpt] = useState(5);

    const results = useMemo(() => {
        const wr_dec = wr / 100;
        const risk_dec = rpt / 100;

        const simulations = 5000;
        let ruin_count = 0;

        for (let sim = 0; sim < simulations; sim++) {
            let eq = cap;
            for (let trade = 0; trade < 1000; trade++) {
                if (Math.random() < wr_dec) eq *= (1 + risk_dec * rr);
                else eq *= (1 - risk_dec);

                if (eq < cap * 0.5) {
                    ruin_count++;
                    break;
                }
            }
        }

        const ruin_prob = (ruin_count / simulations) * 100;

        const streak_den = Math.log(1 / (1 - wr_dec));
        const exp_streak = streak_den === 0 ? 0 : Math.round(Math.log(1000) / streak_den);

        const after_streak_cap = cap * Math.pow(1 - risk_dec, exp_streak);
        const streak_drawdown = ((cap - after_streak_cap) / cap) * 100;

        let status = 'Safe';
        let statusColor = 'val-green';
        if (ruin_prob >= 50) {
            status = 'Extremely Dangerous';
            statusColor = 'val-negative';
        } else if (ruin_prob >= 20) {
            status = 'Dangerous';
            statusColor = 'val-negative';
        } else if (ruin_prob >= 5) {
            status = 'Moderate Risk';
            statusColor = 'val-amber';
        }

        const ruinColor = ruin_prob >= 20 ? 'val-negative' : ruin_prob >= 5 ? 'val-amber' : 'val-green';

        return { ruin_prob, exp_streak, after_streak_cap, streak_drawdown, status, statusColor, ruinColor };
    }, [wr, rr, cap, rpt]);

    return (
        <div className="tct-tool-container">
            <div className="tct-panel-title">Risk of Ruin Calculator</div>

            <div className="tct-input-wrap d-flex flex-wrap gap-2">
                <InputGroup label="Win Rate %" value={wr} onChange={setWr} step="1" />
                <InputGroup label="Reward:Risk Ratio" value={rr} onChange={setRr} step="0.1" />
                <InputGroup label="Capital" value={cap} onChange={setCap} />
                <InputGroup label="Risk Per Trade %" value={rpt} onChange={setRpt} step="0.5" />
            </div>

            <div className="tct-results-grid mt-4">

                {/* Card 1: Ruin probability */}
                <div className="tct-card">
                    <div className="tct-data-row">
                        <span className="tct-label">Ruin Prob (50% Drop)</span>
                        <span className={`tct-value big-value ${results.ruinColor}`}>
                            {results.ruin_prob.toFixed(2)}%
                        </span>
                    </div>
                    <div className="tct-data-row">
                        <span className="tct-label">Risk Level</span>
                        <span className={`tct-value ${results.statusColor}`} style={{ textTransform: 'uppercase' }}>
                            {results.status}
                        </span>
                    </div>
                    <div className="tct-data-row">
                        <span className="tct-label">Suggested Risk</span>
                        <span className="tct-value val-green" style={{ fontWeight: 700 }}>
                            0.5% – 1%
                        </span>
                    </div>
                </div>

                <div className="tct-card tct-amber">
                    <div className="tct-data-row">
                        <span className="tct-label">Expected Streak (in 1000 trades)</span>
                        <span className="tct-value big-value val-amber">
                            {results.exp_streak} trades
                        </span>
                    </div>
                    <div className="tct-data-row">
                        <span className="tct-label">Capital After Streak</span>
                        <span className="tct-value val-negative">
                            ₹{fmtCur.format(results.after_streak_cap)}
                        </span>
                    </div>
                    <div className="tct-data-row">
                        <span className="tct-label">Streak Drawdown</span>
                        <span className="tct-value big-value val-negative">
                            {results.streak_drawdown.toFixed(2)}%
                        </span>
                    </div>
                </div>

            </div>

            <InfoBox
                title="Risk of Ruin in Trading: How Traders Blow Up Their Accounts"
                meta="Learn how risk of ruin determines whether your trading strategy will eventually destroy your account."
                sections={[
                    {
                        title: 'Introduction',
                        content: 'Risk of ruin measures the probability that a trader will lose all capital or reach an unacceptable drawdown (50% threshold used here).<br><br>It depends on:<br>• win rate<br>• risk per trade<br>• reward risk ratio',
                    },
                    {
                        title: 'Example',
                        content: 'Trader A risks 5% per trade.<br>After several losing trades, capital can drop significantly.<br><br>Trader B risks 0.5% per trade.<br>The probability of account blow up becomes extremely small.',
                    },
                    {
                        title: 'Why It Matters',
                        content: 'Professional traders focus on capital survival first.<br>Because survival allows compounding.',
                    },
                ]}
            />
        </div>
    );
};

export default RuinTool;